<template>
    <div class="auth-container">
        <div class="auth-header">
            <AppLogoForAuth />
            <h2 class="auth-title text-cost-blue">Reset Your Password</h2>
            <p class="auth-subtitle">
                Enter your email address and we'll send you a link to reset your password.
            </p>
        </div>

        <form @submit.prevent="handleReset" class="auth-form">
            <div class="input-group">
                <label for="email" class="input-label">Email Address</label>
                <div class="input-wrapper">
                    <AppIconEnvelope class="input-icon" />
                    <input type="email" id="email" v-model="email" placeholder="member@example.com" class="auth-input"
                        required />
                </div>
            </div>

            <button type="submit" class="auth-button" :disabled="isLoading">
                <span class="flex items-center justify-center">
                    <svg v-if="isLoading" class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg"
                        fill="none" viewBox="0 0 24 24">
                        <!-- Loading spinner SVG -->
                    </svg>
                    <span>{{ isLoading ? 'Sending...' : 'Send Reset Link' }}</span>
                </span>
            </button>

            <div v-if="successMessage" class="success-message">
                <AppIconCheckCircle class="success-icon" />
                {{ successMessage }}
            </div>

            <div v-if="errorMessage" class="error-message">
                <AppIconExclamationTriangle class="error-icon" />
                {{ errorMessage }}
            </div>
        </form>

        <div class="auth-footer">
            <NuxtLink to="/auth/login" class="auth-link">
                <AppIconArrowLeft class="link-arrow" />
                Back to Login
            </NuxtLink>
        </div>
    </div>
</template>

<script setup>
definePageMeta({
    layout: 'auth',
    public: true, 
    middleware: ['redirect-if-authenticated'] 
})

const email = ref('')
const isLoading = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const handleReset = async () => {
    errorMessage.value = ''
    successMessage.value = ''
    isLoading.value = true

    try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000))

        successMessage.value = 'Password reset link sent! Check your email.'
        email.value = ''
    } catch (error) {
        errorMessage.value = error.message || 'Failed to send reset link. Please try again.'
    } finally {
        isLoading.value = false
    }
}
</script>

<style scoped>
.auth-container {
    @apply max-w-md mx-auto p-8 bg-white rounded-xl shadow-lg;
}

/* .brand-title {
    @apply text-4xl font-bold text-center mb-4;
} */

.auth-title {
    @apply text-2xl font-semibold text-center mb-2;
}

.auth-subtitle {
    @apply text-sm text-gray-600 text-center mb-8;
}

.auth-form {
    @apply space-y-6;
}

.input-group {
    @apply space-y-2;
}

.input-label {
    @apply block text-sm font-medium text-gray-700;
}

.input-wrapper {
    @apply relative;
}

.input-icon {
    @apply w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400;
}

.auth-input {
    @apply w-full px-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cost-red focus:border-cost-red;
}

.auth-button {
    @apply w-full bg-cost-red text-white py-3 rounded-lg hover:bg-red-600 transition-colors font-semibold disabled:bg-red-300 disabled:cursor-not-allowed;
}

.success-message {
    @apply flex items-center text-sm text-green-600 bg-green-50 p-3 rounded-lg;
}

.success-icon {
    @apply w-5 h-5 mr-2;
}

.error-message {
    @apply flex items-center text-sm text-red-600 bg-red-50 p-3 rounded-lg;
}

.error-icon {
    @apply w-5 h-5 mr-2;
}

.auth-footer {
    @apply pt-4 border-t border-gray-200 mt-6;
}

.auth-link {
    @apply flex items-center justify-center text-cost-red hover:text-red-600 transition-colors text-sm font-medium;
}

.link-arrow {
    @apply w-4 h-4 mr-2;
}

.text-cost-red {
    color: #e31936;
}

.text-cost-blue {
    color: #005dab;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.animate-spin {
    animation: spin 1s linear infinite;
}
</style>